import styled from "styled-components";
import {
  DataGridPremium,
  GridColDef,
  GridValidRowModel,
  GridColType,
  GridRenderCellParams,
  GridTreeNodeWithRender,
  GridValueFormatter,
  GridValueGetter,
  DataGridPremiumProps,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GridGroupingColDefOverride,
  GridAggregationModel,
} from "@mui/x-data-grid-premium";
import CustomToolbar from "./CustomToolbar";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import {
  MutableRefObject,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import CustomColumnMenu from "./CustomColumnMenu";
import { GridInitialStatePremium } from "@mui/x-data-grid-premium/models/gridStatePremium";
import { t } from "i18next";
import {
  defaultRenderCell,
  defaultRenderCellWithAggregation,
  defaultValueGetter,
} from "./CustomColumnsAggregation";
import { colors } from "../../../styles/figmaColors";
import { SearchBarDS, SearchBarDSProps } from "../SearchBar";
import { capitalize } from "../../../format";
import { EmptyTableComponent } from "../table/EmptyTableComponent";
import MaterialUiThemeProvider from "../../../providers/MaterialUiThemeProvider";
import { Spacer } from "../Spacer";
import { MenuOptionsProps } from "../Menu/MenuOptions";
import { MenuOptionsCell } from "./menuOptionsCell";
import { MonthPeriod } from "../materialDatePicker/MaterialUiMonthPicker";

export type StatusSeverity = "error" | "warning" | "info" | "success";

type MaterialUiTableRow = GridValidRowModel & {
  statusSeverity?: StatusSeverity;
};

type MaterialUiTableProps<
  Key extends string,
  T extends Record<Key, string | number | null | boolean>,
> = {
  rows: MaterialUiTableRow[];
  columns: {
    [index in keyof T]: MaterialUiColumnsProps;
  };
  apiRef: MutableRefObject<GridApiPremium>;
  onRowClickAction?: (row: T) => void;
  dataGridState: GridInitialStatePremium;
  searchBar: SearchBarDSProps<T>;
  downloadViewAction?: (file: Buffer | undefined) => void;
  downloadDetailsAction?: () => void;
  pageName: string;
  cta?: {
    title: string;
    action: () => void;
    width?: number;
    isDisabled?: boolean;
    leftIcon?: JSX.Element | null;
  };
  cta2?: {
    title: string;
    action: () => void;
    width?: number;
    isDisabled?: boolean;
    leftIcon?: JSX.Element | null;
  };
  setRowHoverUuid?: (id: string | null) => void;
  rowToSendToTop?: string | null;
  rowsChecked?: T[];
  setRowsChecked?: (rows: T[]) => void;
  groupingColumn?: string[];
  groupingFirstColumnDisplayed?: string;
  menuOptions?: MenuOptionsProps<Key, T>;
  dateRange?: {
    dateFiltersCallBack: (dates: Date[]) => void;
    dateRangeCallBack: [start: string, end: string] | undefined;
  };
  singleDate?: {
    singleDateFilterCallBack: (date: Date) => void;
    singleDateCallBack: Date | undefined;
    title?: string;
  };
  monthPicker?: {
    monthFilterCallBack: (dates: MonthPeriod) => void;
    monthFilterValue: MonthPeriod | undefined;
  };
};

export type MaterialUiColumnsProps = {
  type: GridColType | undefined;
  valueFormatter?:
    | GridValueFormatter<GridValidRowModel, unknown, unknown, never>
    | undefined;
  renderCell?:
    | ((
        params: GridRenderCellParams<
          GridValidRowModel,
          unknown,
          unknown,
          GridTreeNodeWithRender
        >,
      ) => ReactNode)
    | undefined;
  valueGetter?:
    | GridValueGetter<GridValidRowModel, unknown, unknown, never>
    | undefined;
  width?: number;
};

const formatColumns = <
  Key extends string,
  T extends Record<Key, string | number | null | boolean>,
>(
  columnsDisplayed: { [index in keyof T]: MaterialUiColumnsProps },
  dataGridState: GridInitialStatePremium,
  pageName: string,
  menuOptions?: MenuOptionsProps<Key, T>,
) => {
  const columnsKeys = Object.keys(columnsDisplayed);
  const columns: Record<string, GridColDef<GridValidRowModel>> = {};

  columnsKeys.forEach((k) => {
    const type = columnsDisplayed[k as keyof T].type;
    const width = columnsDisplayed[k as keyof T].width;
    const valueFormatter = columnsDisplayed[k as keyof T].valueFormatter;
    const renderCellParams = columnsDisplayed[k as keyof T].renderCell;
    const valueGetterParams = columnsDisplayed[k as keyof T].valueGetter;

    const currentAggregation =
      dataGridState.aggregation?.model?.[k as keyof GridAggregationModel] ??
      null;

    const renderCell = renderCellParams
      ? renderCellParams
      : currentAggregation
        ? currentAggregation &&
          defaultRenderCellWithAggregation[currentAggregation]?.renderCell
        : type
          ? defaultRenderCell[type]?.renderCell
          : undefined;

    const valueGetter = valueGetterParams
      ? valueGetterParams
      : type
        ? defaultValueGetter[type]?.valueGetter
        : undefined;

    columns[k] = {
      field: k.toString(),
      headerName: capitalize(t(`${pageName}.column.${k}`) ?? k.toString()),
      sortable: true,
      pinnable: false,
      groupable: false,
      filterable: true,
      aggregable: type !== "date",
      width: width ?? 175,
      type,
      valueFormatter,
      valueGetter:
        (valueGetter as GridValueGetter<
          GridValidRowModel,
          unknown,
          unknown,
          never
        >) ?? undefined,
      renderCell:
        (renderCell as (
          params: GridRenderCellParams<
            GridValidRowModel,
            unknown,
            unknown,
            GridTreeNodeWithRender
          >,
        ) => ReactNode) ?? undefined,
    };
  });

  if (
    !!menuOptions &&
    menuOptions?.options.length > 0 &&
    columnsKeys.length === Object.keys(columns).length
  ) {
    columns["optionsMenu"] = {
      field: "optionsMenu",
      headerName: "",
      width: 80,
      sortable: false,
      pinnable: false,
      groupable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        return (
          params.row &&
          (params.row.id as string) && (
            <MenuOptionsCell
              id={params.id as string}
              menuOptions={menuOptions}
            />
          )
        );
      },
    };
    columnsKeys.push("optionsMenu");
  }

  // Retrieve columns order from storage
  const orderedFields = dataGridState?.columns?.orderedFields ?? [];
  // Complete columns if doesn't exist last time
  columnsKeys.forEach((key) => {
    if (!orderedFields.includes(key)) orderedFields.push(key);
  });

  return orderedFields
    .filter((key) => columns[key]?.field)
    .map((key) => ({
      ...columns[key],
      // restore resized columns
      width: dataGridState?.columns?.dimensions
        ? dataGridState.columns.dimensions[key]?.width
        : columns[key].width,
    }));
};

export const MaterialUiTable = <
  Key extends string,
  T extends Record<Key, string | number | null | boolean>,
>({
  rows,
  columns,
  onRowClickAction,
  dataGridState,
  searchBar,
  downloadViewAction,
  downloadDetailsAction,
  apiRef,
  pageName,
  cta,
  cta2,
  setRowHoverUuid,
  rowToSendToTop,
  rowsChecked,
  setRowsChecked,
  groupingColumn,
  groupingFirstColumnDisplayed,
  menuOptions,
  dateRange,
  singleDate,
  monthPicker,
  ...dataGridProps
}: Omit<DataGridPremiumProps, "rows" | "columns"> &
  MaterialUiTableProps<Key, T>) => {
  const rowHeight = 70;
  const apiRefCurrent = apiRef?.current;

  const [isSearchResult, setIsSearchResult] = useState<boolean>(false);
  const [clickedRowId, setClickedRowId] = useState<string | null>(null);
  const [hasFirstColumnPinned, setHasFirstColumnPinned] =
    useState<boolean>(false);

  const getRowSelectionModel = (): (string | number)[] => {
    if (!rowsChecked) return [];
    return rowsChecked.map((row) => {
      const rowWithId = row as T & { id: string | number };
      return rowWithId.id;
    });
  };

  const columnPinnedAction = useCallback(() => {
    setHasFirstColumnPinned(!hasFirstColumnPinned);
  }, [hasFirstColumnPinned]);

  const setSearchValues = (values: T[]) => {
    searchBar?.setFilterValues(values);
    setIsSearchResult(values.length === 0);
  };
  const columnsFormatted = formatColumns(
    columns,
    dataGridState,
    pageName,
    menuOptions,
  );

  const rowsWithIndex: (T & { index: number })[] = rows.map((r, index) => {
    return { ...(r as T), index: index };
  });
  let rowsWithIndexSorted: (T & { index: number })[] = rowsWithIndex;

  const pinnedColumns = useMemo(() => {
    if (!apiRefCurrent?.getAllColumns) return;
    const columns = apiRefCurrent.getVisibleColumns();

    return hasFirstColumnPinned ? { left: [columns[0].field] } : {};
  }, [apiRefCurrent, hasFirstColumnPinned]);

  useEffect(() => {
    if (!apiRefCurrent) return;
    const apiRefIsLoaded = Object.keys(apiRefCurrent).length !== 0;
    if (rowsWithIndex.length === 0 || !apiRefIsLoaded) return;

    // eslint-disable-next-line react-hooks/exhaustive-deps
    rowsWithIndexSorted = apiRefCurrent.getSortedRows().map((r, index) => {
      return { ...(r as T), index: index };
    });
  }, [apiRefCurrent, rowsWithIndex]);

  useEffect(() => {
    if (!rowToSendToTop) return;
    const rowIndex =
      rowsWithIndexSorted.find(
        (row) => "id" in row && row["id"] === rowToSendToTop,
      )?.index ?? null;
    if (!rowIndex) return;

    const rowPosition = rowIndex * rowHeight; // 70px height of the row
    setTimeout(() => {
      apiRefCurrent?.scroll({
        top: rowPosition,
      });
    }, 200);
  }, [apiRefCurrent, rowToSendToTop, rowsWithIndexSorted]);

  const groupingColDef: GridGroupingColDefOverride<GridValidRowModel> = {
    leafField: groupingFirstColumnDisplayed,
  };

  return (
    <StyledContainer>
      <StyledHeaderContainer>
        <Spacer y={0.5} />
        <SearchBarDS
          setFilterValues={setSearchValues}
          placeholder={t("home.searchbar")}
          values={searchBar.values}
          keysToTranslate={searchBar.keysToTranslate}
          findKeysToTranslate={searchBar.findKeysToTranslate}
          keysToIgnore={searchBar.keysToIgnore}
        />
      </StyledHeaderContainer>
      <MaterialUiThemeProvider>
        <StyledDataGrid
          {...dataGridProps}
          apiRef={apiRef}
          rows={rowsWithIndexSorted}
          columns={columnsFormatted}
          getRowHeight={({ densityFactor }) => rowHeight * densityFactor}
          pinnedColumns={pinnedColumns}
          checkboxSelection={!!(rowsChecked && setRowsChecked)}
          disableRowSelectionOnClick={true}
          onRowSelectionModelChange={(newSelection) => {
            if (!rowsChecked || !setRowsChecked) return;
            const selectedRows = rowsWithIndexSorted.filter((row) => {
              const rowWithId = row as unknown as T & { id: string | number };
              return newSelection.includes(rowWithId.id);
            });
            setRowsChecked(selectedRows);
          }}
          rowSelectionModel={getRowSelectionModel()}
          slots={{
            toolbar: () => (
              <>
                <Spacer y={0.5} />
                <CustomToolbar
                  downloadViewAction={downloadViewAction}
                  downloadDetailsAction={downloadDetailsAction}
                  pageName={pageName}
                  apiRef={apiRef}
                  dateFiltersFields={Object.keys(columns).filter(
                    (k) => columns[k as keyof typeof columns].type === "date",
                  )}
                  dateRange={dateRange}
                  singleDate={singleDate}
                  monthPicker={monthPicker}
                  columnPinnedAction={columnPinnedAction}
                  hasFirstColumnPinned={hasFirstColumnPinned}
                />
              </>
            ),
            columnMenu: CustomColumnMenu,
            noRowsOverlay: () => (
              <EmptyTableComponent
                pageName={pageName}
                isSearchResult={isSearchResult}
                cta={cta}
                cta2={cta2}
              />
            ),
          }}
          slotProps={{
            row: {
              onMouseEnter: (e) =>
                setRowHoverUuid?.(e.currentTarget.dataset.id ?? null),
              onMouseLeave: () => setRowHoverUuid?.(null),
            },
          }}
          onRowClick={(params) => {
            onRowClickAction && onRowClickAction(params.row as T);
            setClickedRowId((params.row as T & { id: string }).id);
          }}
          getRowClassName={(params) => {
            const isRowSelected =
              clickedRowId === (params.row as T & { id: string }).id;
            const statusSeverity = (params.row as MaterialUiTableRow)
              ?.statusSeverity;
            const baseClass = statusSeverity
              ? `super-app-theme--${statusSeverity}`
              : "super-app-theme";
            return `${baseClass}${isRowSelected ? "--is-row-clicked" : ""}`;
          }}
          groupingColDef={
            groupingFirstColumnDisplayed ? groupingColDef : undefined
          }
          initialState={{
            ...dataGridState,
            rowGrouping: groupingColumn && { model: groupingColumn },
            sorting: {
              sortModel: [
                { field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: "asc" },
              ],
            },
          }}
          hideFooter
          sx={{
            border: "none",
            "& .MuiDataGrid-virtualScroller": {
              cursor: onRowClickAction ? "pointer" : "default",
              border: `0.0625rem solid ${colors["colors/borders/cells/cells"]}`,
              borderRadius: 2,
            },
            "& .MuiDataGrid-row": {
              "--rowBorderColor": `${colors["colors/borders/cells/cells"]}`,
            },
            "& .MuiDataGrid-cell[aria-colindex]:not([aria-colindex='1'])": {
              borderLeft: `0.0625rem solid ${colors["colors/borders/cells/cells"]} !important`,
            },
            "& .MuiDataGrid-cell": {
              outline: "none !important",
              paddingLeft: "1.5rem",
              paddingRight: "1.5rem",
            },
            "& .MuiDataGrid-columnHeaders": {
              color: colors["colors/text/darkGrey"],
              fontSize: "0.75rem",
              fontWeight: "500",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "1rem 1.5rem 1rem 1.5rem",
            },
            '& .MuiDataGrid-columnHeader[aria-colindex]:not([aria-colindex="1"])':
              {
                borderLeft: `0.0625rem solid ${colors["colors/borders/cells/cells"]} !important`,
              },
            "& .MuiDataGrid-columnSeparator--resizable": {
              color: "transparent",
            },
            "& .MuiDataGrid-columnHeader:focus-within": {
              outline: "none !important",
            },
            "& .MuiDataGrid-main": {
              height: rows.length === 0 ? "20rem" : "",
            },
            "& .MuiDivider-root": {
              border: "none",
            },
            "& [class*='is-row-clicked']": {
              background: `linear-gradient(to right, ${colors["colors/system/success/success_light"]} 0.2rem, #F5F5F5 0.2rem);`,
            },
            "& [class*='is-row-clicked'] .MuiDataGrid-cell--pinnedLeft": {
              background: `linear-gradient(to right, ${colors["colors/system/success/success_light"]} 0.2rem, #F5F5F5 0.2rem);`,
            },
            ".MuiDataGrid-aggregationColumnHeaderLabel": {
              display: "none",
            },
            ".MuiDataGrid-container--bottom [role=row]": {
              cursor: "default",
            },
            "& .MuiDataGrid-groupingCriteriaCell": {
              backgroundColor:
                colors["colors/surfaces/background/background_level2"],
            },
            "& .MuiDataGrid-columnHeaderCheckbox": {
              padding: "0rem",
            },
          }}
        />
      </MaterialUiThemeProvider>
    </StyledContainer>
  );
};

const StyledHeaderContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  position: fixed;
  z-index: 5;
`;

const StyledContainer = styled.div`
  display: flex;
  overflow-x: auto;
  overflow-y: hidden;
  height: 100%;
`;

const StyledDataGrid = styled(DataGridPremium)(() => ({
  "& [class*='super-app-theme--error']": {
    backgroundColor: colors["colors/system/error/error_veryLight"],
    "&:hover": {
      backgroundColor: colors["colors/system/error/error_normal"],
    },
    "&.Mui-selected": {
      backgroundColor: colors["colors/system/error/error_dark"],
      "&:hover": {
        backgroundColor: colors["colors/system/error/error_normal"],
      },
    },
  },
  "& [class*='super-app-theme--warning']": {
    backgroundColor: colors["colors/system/warning/warning_ultraLight"],

    "&:hover": {
      backgroundColor: colors["colors/system/warning/warning_normal"],
    },
    "&.Mui-selected": {
      backgroundColor: colors["colors/system/warning/warning_dark"],
      "&:hover": {
        backgroundColor: colors["colors/system/warning/warning_normal"],
      },
    },
  },
  "& [class*='super-app-theme--info']": {
    backgroundColor: colors["colors/system/informative/informative_ultraLight"],
    "&:hover": {
      backgroundColor: colors["colors/system/informative/informative_normal"],
    },
    "&.Mui-selected": {
      backgroundColor: colors["colors/system/informative/informative_dark"],
      "&:hover": {
        backgroundColor: colors["colors/system/informative/informative_normal"],
      },
    },
  },
  "& [class*='super-app-theme--success']": {
    backgroundColor: colors["colors/system/success/success_ultraLight"],
    "&:hover": {
      backgroundColor: colors["colors/system/success/success_normal"],
    },
    "&.Mui-selected": {
      backgroundColor: colors["colors/system/success/success_dark"],
      "&:hover": {
        backgroundColor: colors["colors/system/success/success_normal"],
      },
    },
  },
}));
